import type {
  Action,
  ActionResult,
  TimelineAction,
} from "../types/actions";
import type { Project } from "../types";
import type {
  MutableTimeline,
  MutableTrack,
} from "../utils/immutable-updates";
import { ActionHistory } from "./action-history";
import { snapshotForInverse } from "./snapshot";
import { ActionValidator, validateTrackVolumeParams } from "./action-validator";
import {
  createInverseAction,
  getActionHandler,
  registerActionHandler,
} from "./registry";
import type { ActionHandler } from "./registry";
import "./handlers";
import { clampTrackVolume, resolveTrackVolume } from "../types/timeline";
import { calculateProjectDuration } from "../timeline/project-duration";

/**
 * Track gain, the level every clip on the track is multiplied by.
 *
 * Registered rather than added to the applyTrackAction switch: the registry is
 * consulted first by the executor, the validator and the inverse generator, so
 * one object holds apply, validate and the undo step instead of three edits in
 * three files. The inverse carries the level the track had before, which is how
 * undo after a slider drag lands back on the old number.
 */
const trackVolumeHandler: ActionHandler = {
  type: "track/volume",

  apply(action, project) {
    const params = action.params as { trackId: string; volume: number };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
      track.id === params.trackId
        ? { ...track, volume: clampTrackVolume(params.volume) }
        : track,
    );
  },

  validate(action, project) {
    const errors = validateTrackVolumeParams(
      action.params as { trackId?: unknown; volume?: unknown },
      project,
    );
    return { valid: errors.length === 0, errors };
  },

  invert(action, projectBefore) {
    const params = action.params as { trackId: string };
    const track = projectBefore.timeline.tracks.find(
      (candidate) => candidate.id === params.trackId,
    );
    if (!track) return null;

    // resolveTrackVolume, not track.volume: a track that never had a level set
    // undoes back to unity rather than to undefined.
    return createInverseAction(action, "track/volume", {
      trackId: params.trackId,
      volume: resolveTrackVolume(track),
    });
  },
};

registerActionHandler(trackVolumeHandler);

export class ActionExecutor {
  private validator: ActionValidator;
  private history: ActionHistory;
  private lastAddedIds: Map<string, string> = new Map();

  constructor(history?: ActionHistory) {
    this.validator = new ActionValidator();
    this.history = history || new ActionHistory();
  }

  async execute(action: Action, project: Project): Promise<ActionResult> {
    const validationResult = this.validator.validate(action, project);

    if (!validationResult.valid) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: validationResult.errors.map((e) => e.message).join("; "),
          details: { errors: validationResult.errors },
        },
      };
    }
    // Not a JSON round trip, which turned a Blob into {} and a Float32Array
    // into an object with numeric keys, and not structuredClone, which throws
    // on the functions a project can carry. See snapshot.ts.
    const projectSnapshot = snapshotForInverse(project);
    const inverseAction =
      getActionHandler(action.type)?.invert(action, projectSnapshot) ?? null;
    try {
      await this.applyAction(action as TimelineAction, project);
      // Resolve generated-id markers while this action's newly-created entity is
      // still the latest one. Deferring resolution until undo makes every entry
      // in a grouped add operation target the final created entity instead.
      const resolvedInverseAction = inverseAction
        ? this.resolveSpecialMarkers(inverseAction)
        : null;
      this.history.push(action, resolvedInverseAction);

      return {
        success: true,
        actionId: action.id,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
        },
      };
    }
  }

  async executeMany(
    actions: Action[],
    project: Project,
  ): Promise<ActionResult[]> {
    const results: ActionResult[] = [];

    for (const action of actions) {
      const result = await this.execute(action, project);
      results.push(result);
      if (!result.success) {
        break;
      }
    }

    return results;
  }

  async undo(project: Project): Promise<ActionResult> {
    if (!this.history.canUndo()) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: "Nothing to undo",
        },
      };
    }

    const inverseActions = this.history.undoGroup();
    if (inverseActions.length === 0) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: "No inverse action available",
        },
      };
    }

    try {
      for (const inverseAction of inverseActions) {
        const resolvedAction = this.resolveSpecialMarkers(inverseAction);
        await this.applyAction(resolvedAction as TimelineAction, project);
      }
      return { success: true, actionId: inverseActions[0].id };
    } catch (error) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: error instanceof Error ? error.message : "Undo failed",
        },
      };
    }
  }

  async redo(project: Project): Promise<ActionResult> {
    if (!this.history.canRedo()) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: "Nothing to redo",
        },
      };
    }

    const actions = this.history.redoGroup();
    if (actions.length === 0) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: "No action to redo",
        },
      };
    }

    try {
      for (const action of actions) {
        await this.applyAction(action as TimelineAction, project);
      }
      return { success: true, actionId: actions[0].id };
    } catch (error) {
      return {
        success: false,
        error: {
          code: "INVALID_PARAMS",
          message: error instanceof Error ? error.message : "Redo failed",
        },
      };
    }
  }

  getHistory(): ActionHistory {
    return this.history;
  }

  private resolveSpecialMarkers(action: Action): Action {
    const params = { ...action.params } as Record<string, unknown>;

    for (const [key, value] of Object.entries(params)) {
      if (value === "__LAST_ADDED__") {
        const lastId = this.lastAddedIds.get(action.type.split("/")[0]);
        if (lastId) {
          params[key] = lastId;
        }
      }
    }

    return { ...action, params };
  }

  private async applyAction(
    action: TimelineAction,
    project: Project,
  ): Promise<void> {
    const type = action.type;

    const handler = getActionHandler(type);
    if (!handler) {
      // Every action type is a registered handler now, so reaching here means
      // a type nothing owns. Throwing is the point: this is also the undo and
      // redo path, and a silent no-op there reports a restore that never
      // happened.
      throw new Error(`Unhandled action type ${type}`);
    }

    await handler.apply(action as Action, project, {
      lastAddedIds: this.lastAddedIds,
    });
    this.recalculateTimelineDuration(project);
  }

  private recalculateTimelineDuration(project: Project): void {
    (project.timeline as MutableTimeline).duration =
      calculateProjectDuration(project);
  }

}
